// Archieflab — filter sidebar: show-all expansion + in-list search.
// Each <af-subgroup> hosts a collapsed list, an expand button, and an
// expanded panel containing search + a scrollable alphabetical list.
// Form auto-submit is handled by the existing dynamic-product-search controller.

class ArchiefSubgroup extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this.collapsedList = this.querySelector('[data-role="collapsed"]');
    this.expanded = this.querySelector('.af-expanded');
    this.expandBtn = this.querySelector('[data-action="expand"]');
    this.collapseBtn = this.querySelector('[data-action="collapse"]');
    this.searchInput = this.querySelector('[data-role="search"]');
    this.searchClear = this.querySelector('[data-action="clear-search"]');
    this.fullList = this.querySelector('[data-role="full"]');
    this.empty = this.querySelector('.af-empty');

    this.expandBtn?.addEventListener('click', () => this.toggle(true));
    this.collapseBtn?.addEventListener('click', () => this.toggle(false));
    this.searchInput?.addEventListener('input', (e) => this.filter(e.target.value));
    this.searchClear?.addEventListener('click', () => {
      this.searchInput.value = '';
      this.filter('');
      this.searchInput.focus();
    });
  }

  toggle(expand) {
    if (!this.expanded) return;
    this.collapsedList.hidden = expand;
    if (this.expandBtn) this.expandBtn.hidden = expand;
    this.expanded.hidden = !expand;
    if (expand) {
      this.searchInput?.focus();
    } else {
      this.searchInput && (this.searchInput.value = '');
      this.filter('');
    }
  }

  filter(query) {
    if (!this.fullList) return;
    const q = query.trim().toLowerCase();
    if (this.searchClear) this.searchClear.hidden = !q;
    let visible = 0;
    this.fullList.querySelectorAll('li').forEach((li) => {
      const hay = li.dataset.search || '';
      const match = !q || hay.includes(q);
      li.hidden = !match;
      if (match) visible += 1;
    });
    if (this.empty) this.empty.hidden = visible !== 0;
  }
}
customElements.define('af-subgroup', ArchiefSubgroup);

// Wrapper: nothing today; reserved for future cross-subgroup behaviour.
class ArchiefFilters extends HTMLElement {}
customElements.define('archief-filters', ArchiefFilters);

// Decade buttons (Periode): toggle all corresponding hidden year checkboxes in one go.
// Delegated via document so it survives dynamic-product-search re-renders.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.af-opt--decade');
  if (!btn) return;
  const csv = (btn.dataset.decadeYears || '').trim().replace(/,$/, '');
  if (!csv) return;
  const years = csv.split(',').filter(Boolean);
  const form = btn.closest('form');
  if (!form) return;
  const newState = btn.dataset.state !== 'on'; // partial or off → switch on; on → off
  years.forEach((y) => {
    const inp = form.querySelector(`input[data-year="${CSS.escape(y)}"]`);
    if (inp) inp.checked = newState;
  });
  // Trigger the form's auto-submit (data-submit-on-change).
  const anyInput = form.querySelector('input[data-year]');
  if (anyInput) {
    anyInput.dispatchEvent(new Event('change', { bubbles: true }));
  } else {
    form.dispatchEvent(new Event('change', { bubbles: true }));
  }
});

// Active-filter pills (X): make sure the X click reliably removes the filter, even
// after dynamic-product-search swaps in new markup whose anchors aren't bound to the
// theme's link handler. Capture-phase listener so we run before any stale handler.
document.addEventListener(
  'click',
  (e) => {
    const a = e.target.closest('a.collection__filters-active__filter');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;
    const dps = document.querySelector('dynamic-product-search');
    if (dps && typeof dps.renderCollection === 'function') {
      e.preventDefault();
      e.stopImmediatePropagation();
      const url = new URL(href, window.location.origin);
      const qs = url.search.startsWith('?') ? url.search.slice(1) : url.search;
      dps.renderCollection({ query: qs });
    }
    // Otherwise: let the browser navigate natively to the href.
  },
  true
);

// Sort dropdown: show the selected option text alongside the "Sorteren op:" label
// so the whole pill (including the label) reads naturally. The <select> itself is
// rendered transparent and absolutely positioned over the pill, so clicks anywhere
// open the native dropdown.
function syncSortCurrent() {
  document
    .querySelectorAll('.collection-main__sort__selector .select__select')
    .forEach((select) => {
      const wrapper = select.closest('.collection-main__sort__selector');
      if (!wrapper) return;
      let current = wrapper.querySelector('.sort-current');
      if (!current) {
        current = document.createElement('span');
        current.className = 'sort-current';
        const label = wrapper.querySelector('.collection-filters__label');
        if (label && label.nextSibling) {
          label.parentNode.insertBefore(current, label.nextSibling);
        } else {
          wrapper.appendChild(current);
        }
      }
      const opt = select.options[select.selectedIndex];
      current.textContent = opt ? opt.text : '';
      if (!select._afBound) {
        select._afBound = true;
        select.addEventListener('change', () => {
          const o = select.options[select.selectedIndex];
          current.textContent = o ? o.text : '';
        });
      }
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncSortCurrent);
} else {
  syncSortCurrent();
}
// Re-sync after dynamic-product-search swaps in new markup.
document.addEventListener('on:dynamic-product-search:loaded', syncSortCurrent);
document.addEventListener('shopify:section:load', syncSortCurrent);
